// client/src/pages/MyURLsPage.js
// My URLs workflow page - contains all URL-level analysis tools
// UPDATED Jan 14 2026: Fixed tab navigation - main section buttons now only switch sections when needed
// UPDATED Jun 22 2026: Added Content Health master table (Model B) as primary overview tab
// UPDATED Aug 12 2026: Removed Legacy Overview, Technical Audits section, and non-entity content analysis tabs per self-service onboarding cleanup

import React, { useState, useEffect } from 'react';
import SidebarLayout from '../components/SidebarLayout';

// Import existing audit components
import NLPEntityDashboard from '../modules/NLPEntityDashboard';
import ContentHealthTable from '../components/ContentHealthTable';

// Load Work Sans font
if (typeof document !== 'undefined') {
  const fontLink = document.createElement('link');
  fontLink.href = 'https://fonts.googleapis.com/css2?family=Work+Sans:wght@300;400;500;600;700;800&display=swap';
  fontLink.rel = 'stylesheet';
  if (!document.querySelector('link[href*="Work+Sans"]')) {
    document.head.appendChild(fontLink);
  }
}

const colors = {
  primary: '#39393a',
  secondary: '#048ba8',
  tertiary: '#e9c46a',
  accent: '#f4a261',
  grey: '#e76f51',
  green: '#01796f'
};

const MyURLsPage = ({ campaign, campaigns, apiBaseUrl }) => {
  const [activeTab, setActiveTab] = useState('health');

  const tabs = [
    { id: 'health',            label: 'Content Health',              group: 'overview', badge: 'NEW' },
    { id: 'entities',          label: 'Entity Extraction and Mapping', group: 'content', description: 'Entity salience and NLP analysis' }
  ];

  const groupedTabs = {
    overview: tabs.filter(t => t.group === 'overview'),
    content: tabs.filter(t => t.group === 'content')
  };

  return (
    <SidebarLayout currentCampaign={campaign} campaigns={campaigns} apiBaseUrl={apiBaseUrl}>
      <div style={{ padding: '24px', fontFamily: "'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
        {/* Top-Level Tab Navigation */}
        <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e5e7eb', marginBottom: '24px' }}>
          <div style={{ borderBottom: '1px solid #e5e7eb' }}>
            <nav style={{ display: 'flex', gap: '32px', padding: '0 24px' }}>
              {[
                { label: 'Overview', group: 'overview', defaultTab: 'health' },
                { label: 'Content Analysis', group: 'content', defaultTab: 'entities' }
              ].map(({ label, group, defaultTab }) => {
                const isActive = groupedTabs[group].some(t => t.id === activeTab);
                return (
                  <button key={group}
                    onClick={() => { if (!isActive) setActiveTab(defaultTab); }}
                    style={{ padding: '16px 0', fontSize: '13px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: isActive ? `2px solid ${colors.primary}` : '2px solid transparent', marginBottom: '-1px', color: isActive ? '#111827' : '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}>
                    {label}
                  </button>
                );
              })}
            </nav>
          </div>

          <div style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
            <nav style={{ display: 'flex', flexWrap: 'wrap', padding: '0 24px' }}>
              {Object.keys(groupedTabs).map(group =>
                groupedTabs[group].some(t => t.id === activeTab) &&
                groupedTabs[group].map(tab => (
                  <TabButton key={tab.id} tab={tab} isActive={activeTab === tab.id} onClick={() => setActiveTab(tab.id)} colors={colors} />
                ))
              )}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'health' && campaign && (
            <ContentHealthTable campaign={campaign} apiBaseUrl={apiBaseUrl} />
          )}

          {activeTab === 'entities' && campaign && (
            <NLPEntityDashboard campaignId={campaign.id} userId={campaign.userId} campaignName={campaign.name} urls={campaign.urls} apiBaseUrl={apiBaseUrl} />
          )}

          {!campaign && (
            <div style={{ backgroundColor: '#fef3c7', border: '1px solid #fde68a', borderRadius: '8px', padding: '16px' }}>
              <p style={{ color: '#78350f', margin: 0 }}>Please select a campaign from the sidebar to view URL audits</p>
            </div>
          )}
        </div>
      </div>
    </SidebarLayout>
  );
};

const TabButton = ({ tab, isActive, onClick, colors }) => {
  const [isHovered, setIsHovered] = React.useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}
      style={{ position: 'relative', padding: '12px 16px', fontSize: '14px', fontWeight: isActive ? '600' : '500', borderBottom: isActive ? `2px solid ${colors.secondary}` : isHovered ? '2px solid #d1d5db' : '2px solid transparent', color: isActive ? '#111827' : isHovered ? '#111827' : '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span>{tab.label}</span>
        {tab.badge && (
          <span style={{ padding: '2px 8px', fontSize: '11px', fontWeight: '600', borderRadius: '9999px', backgroundColor: `${colors.tertiary}40`, color: colors.primary }}>{tab.badge}</span>
        )}
      </div>
    </button>
  );
};

export default MyURLsPage;
